// หน้ายืนยันอีเมล — สวยงาม น่าเชื่อถือ และรองรับการส่งลิงก์ยืนยันอีกครั้ง
import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import ApiService from "../services/ApiService";
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Input,
  FormControl,
  FormLabel,
  FormHelperText,
  Spinner,
  useToast,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Flex,
  Image,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faCircleXmark,
  faEnvelopeCircleCheck,
  faPaperPlane,
  faArrowRight,
  faUserPlus,
} from "@fortawesome/free-solid-svg-icons";

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const tokenFromUrl = searchParams.get("token");

  const [status, setStatus] = useState("idle"); // idle | loading | success | error | no_token
  const [message, setMessage] = useState("");
  const [resendEmail, setResendEmail] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSent, setResendSent] = useState(false);

  // เมื่อมี token ใน URL ให้เรียก API ยืนยัน
  useEffect(() => {
    if (!tokenFromUrl) {
      setStatus("no_token");
      return;
    }
    let cancelled = false;
    setStatus("loading");
    setMessage("");

    const verify = async () => {
      try {
        const url = `/verifyEmail?token=${encodeURIComponent(tokenFromUrl)}`;
        const result = await ApiService({
          method: "GET",
          url,
          sessionId: "",
        });
        if (cancelled) return;
        setStatus("success");
        setMessage(result.message || "ยืนยันอีเมลเรียบร้อยแล้ว");
      } catch (err) {
        if (cancelled) return;
        setStatus("error");
        setMessage(err.message || "ลิงก์ยืนยันไม่ถูกต้องหรือหมดอายุแล้ว");
      }
    };

    verify();
    return () => { cancelled = true; };
  }, [tokenFromUrl]);

  const handleResend = async () => {
    const email = resendEmail.trim();
    if (!email) {
      toast({
        title: "กรุณากรอกอีเมล",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    setResendLoading(true);
    try {
      const result = await ApiService({
        method: "POST",
        url: "/resendVerificationEmail",
        data: { email },
        sessionId: "",
      });
      setResendSent(true);
      toast({
        title: result.message || "ส่งอีเมลยืนยันแล้ว",
        status: "success",
        duration: 6000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: err.message || "ไม่สามารถส่งอีเมลได้",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setResendLoading(false);
    }
  };

  const goToLogin = () => navigate("/login");
  const goToRegister = () => navigate("/registrationfarmer");

  // เลย์เอาต์หลัก: กลางจอ, พื้นหลังอ่อน, การ์ดเดี่ยว
  return (
    <Flex
      minH="100vh"
      align="center"
      justify="center"
      bg="gray.50"
      py={8}
      px={4}
    >
      <Box w="full" maxW="md">
        {/* โลโก้/หัวข้อระบบ */}
        <VStack spacing={3} mb={8} textAlign="center">
          <Image
            src="/logo.png"
            alt="DLD"
            boxSize={{ base: "56px", md: "72px" }}
            objectFit="contain"
          />
          <Heading size="md" color="gray.700">
            ระบบสั่งจองวัคซีนออนไลน์
          </Heading>
          <Text color="gray.600" fontSize="sm">
            สำนักเทคโนโลยีชีวภัณฑ์สัตว์
          </Text>
        </VStack>

        <Card shadow="lg" borderRadius="xl" overflow="hidden">
          <CardHeader
            bg="primary.600"
            color="white"
            py={4}
            textAlign="center"
          >
            <HStack justify="center" spacing={2}>
              <Box as="span" fontSize="xl">
                <FontAwesomeIcon icon={faEnvelopeCircleCheck} />
              </Box>
              <Heading size="md">ยืนยันอีเมล</Heading>
            </HStack>
          </CardHeader>
          <CardBody p={6}>
            {/* กำลังตรวจสอบ */}
            {status === "loading" && (
              <VStack spacing={4} py={6}>
                <Spinner size="xl" color="primary.500" thickness="3px" />
                <Text color="gray.600">กำลังยืนยันอีเมลของคุณ...</Text>
                <Text fontSize="sm" color="gray.500">
                  กรุณารอสักครู่
                </Text>
              </VStack>
            )}

            {/* ยืนยันสำเร็จ */}
            {status === "success" && (
              <VStack spacing={5} align="stretch">
                <Flex justify="center">
                  <Box
                    color="green.500"
                    fontSize="4xl"
                    aria-hidden
                  >
                    <FontAwesomeIcon icon={faCircleCheck} />
                  </Box>
                </Flex>
                <Heading size="sm" textAlign="center" color="gray.800">
                  ยืนยันอีเมลเรียบร้อยแล้ว
                </Heading>
                <Text textAlign="center" color="gray.600">
                  {message}
                </Text>
                <Text fontSize="sm" textAlign="center" color="gray.500">
                  คุณสามารถเข้าสู่ระบบและสั่งจองวัคซีนได้เลย
                </Text>
                <Button
                  colorScheme="blue"
                  size="lg"
                  rightIcon={<FontAwesomeIcon icon={faArrowRight} />}
                  onClick={goToLogin}
                  w="full"
                >
                  เข้าสู่ระบบ
                </Button>
              </VStack>
            )}

            {/* ข้อผิดพลาด (ลิงก์หมดอายุ/ไม่ถูกต้อง) */}
            {status === "error" && (
              <VStack spacing={5} align="stretch">
                <Flex justify="center">
                  <Box color="red.500" fontSize="4xl" aria-hidden>
                    <FontAwesomeIcon icon={faCircleXmark} />
                  </Box>
                </Flex>
                <Heading size="sm" textAlign="center" color="gray.800">
                  ไม่สามารถยืนยันอีเมลได้
                </Heading>
                <Text textAlign="center" color="gray.600">
                  {message}
                </Text>
                <Text fontSize="sm" textAlign="center" color="gray.500">
                  ลิงก์มีอายุ 24 ชั่วโมง — ส่งลิงก์ใหม่ได้ด้านล่าง หรือลงทะเบียนใหม่
                </Text>
                <Button
                  colorScheme="blue"
                  size="lg"
                  rightIcon={<FontAwesomeIcon icon={faArrowRight} />}
                  onClick={goToLogin}
                  w="full"
                  variant="outline"
                >
                  ไปหน้าเข้าสู่ระบบ
                </Button>
                <Divider />
                <ResendSection
                  resendEmail={resendEmail}
                  setResendEmail={setResendEmail}
                  resendLoading={resendLoading}
                  resendSent={resendSent}
                  onResend={handleResend}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<FontAwesomeIcon icon={faUserPlus} />}
                  onClick={goToRegister}
                  w="full"
                  colorScheme="gray"
                >
                  ลงทะเบียนเกษตรกรใหม่
                </Button>
              </VStack>
            )}

            {/* ไม่มี token — เปิดหน้ามาโดยไม่มีลิงก์ */}
            {status === "no_token" && (
              <VStack spacing={5} align="stretch">
                <Flex justify="center">
                  <Box color="primary.500" fontSize="4xl" aria-hidden>
                    <FontAwesomeIcon icon={faEnvelopeCircleCheck} />
                  </Box>
                </Flex>
                <Heading size="sm" textAlign="center" color="gray.800">
                  ยืนยันอีเมล
                </Heading>
                <Text textAlign="center" color="gray.600">
                  กรุณาคลิกลิงก์ในอีเมลที่เราส่งไปให้คุณ เพื่อยืนยันบัญชีและเข้าสู่ระบบได้
                </Text>
                <Text fontSize="sm" textAlign="center" color="gray.500">
                  ไม่ได้รับอีเมล? กรอกอีเมลที่ใช้ลงทะเบียนด้านล่าง แล้วกดส่งอีกครั้ง
                </Text>
                <ResendSection
                  resendEmail={resendEmail}
                  setResendEmail={setResendEmail}
                  resendLoading={resendLoading}
                  resendSent={resendSent}
                  onResend={handleResend}
                />
                <VStack spacing={3} w="full">
                  <Button
                    colorScheme="blue"
                    size="md"
                    variant="outline"
                    onClick={goToLogin}
                    w="full"
                  >
                    ไปหน้าเข้าสู่ระบบ
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={<FontAwesomeIcon icon={faUserPlus} />}
                    onClick={goToRegister}
                    w="full"
                    colorScheme="gray"
                  >
                    ลงทะเบียนเกษตรกรใหม่
                  </Button>
                </VStack>
              </VStack>
            )}
          </CardBody>
        </Card>

        {/* ข้อความด้านล่างการ์ด */}
        <Text
          textAlign="center"
          fontSize="xs"
          color="gray.500"
          mt={6}
        >
          อีเมลยืนยันมีอายุ 24 ชั่วโมง • หากมีปัญหา กรุณาติดต่อผู้ดูแลระบบ
        </Text>
      </Box>
    </Flex>
  );
};

// ส่วนฟอร์มส่งอีเมลยืนยันอีกครั้ง
function ResendSection({
  resendEmail,
  setResendEmail,
  resendLoading,
  resendSent,
  onResend,
}) {
  return (
    <Box w="full">
      <FormControl>
        <FormLabel fontSize="sm" color="gray.700">
          อีเมลที่ใช้ลงทะเบียน
        </FormLabel>
        <VStack spacing={3} align="stretch" w="full">
          <Input
            type="email"
            placeholder="example@email.com"
            value={resendEmail}
            onChange={(e) => setResendEmail(e.target.value)}
            size="md"
            isDisabled={resendSent}
          />
          <Button
            leftIcon={<FontAwesomeIcon icon={faPaperPlane} />}
            colorScheme="blue"
            onClick={onResend}
            isLoading={resendLoading}
            loadingText="กำลังส่ง..."
            isDisabled={resendSent}
            w="full"
          >
            {resendSent ? "ส่งแล้ว" : "ส่งลิงก์ยืนยันอีกครั้ง"}
          </Button>
        </VStack>
        {resendSent && (
          <FormHelperText color="green.600" mt={2}>
            ระบบได้ส่งอีเมลไปแล้ว กรุณาตรวจสอบกล่องจดหมาย (และโฟลเดอร์ Spam)
          </FormHelperText>
        )}
      </FormControl>
    </Box>
  );
}

export default VerifyEmail;
